import React, { useCallback, useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { api, openAssetLabelPdf } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import {
  ChevronLeft, Printer, Edit, Info, Camera, QrCode,
  CheckCircle2, Package, ArrowRightLeft, PlusCircle, Wrench, X,
  Clock, AlertCircle, Send, Upload, Copy, Trash2,
} from 'lucide-react';

const STATUS_COLOR = {
  Available:   'bg-emerald-100 text-emerald-700',
  'In use':    'bg-blue-100 text-blue-700',
  'In repair': 'bg-amber-100 text-amber-700',
  'Return pending confirmation': 'bg-orange-100 text-orange-700',
  Lost:        'bg-rose-100 text-rose-700',
  Sold:        'bg-slate-100 text-slate-500',
  Scrapped:    'bg-slate-100 text-slate-500',
};

const EVENT_META = {
  listed:             { icon: PlusCircle,     label: 'Listed' },
  edited:             { icon: Edit,           label: 'Edited' },
  assigned:           { icon: ArrowRightLeft, label: 'Assigned' },
  taken:              { icon: Package,        label: 'Taken' },
  returned:           { icon: Package,        label: 'Return initiated' },
  transfer_requested: { icon: Send,           label: 'Transfer requested' },
  transfer_confirmed: { icon: CheckCircle2,   label: 'Transfer confirmed' },
  transfer_rejected:  { icon: X,              label: 'Transfer rejected' },
  status_changed:     { icon: Info,           label: 'Status changed' },
  correction:         { icon: Edit,           label: 'Correction' },
  label_printed:      { icon: Printer,        label: 'Label printed' },
};

const CONDITIONS = ['New', 'Good', 'Fair', 'Poor'];
const TRANSFER_REASONS = ['New joiner', 'Replacement', 'Handover', 'Resignation', 'Repair', 'Return to office', 'Other'];

function fmtDateTime(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
  catch { return iso; }
}

async function fileToDataUrl(file, maxDim = 1600, quality = 0.82) {
  const data = await new Promise((resolve, reject) => {
    const r = new FileReader(); r.onload = () => resolve(r.result); r.onerror = reject;
    r.readAsDataURL(file);
  });
  if (!file.type.startsWith('image/')) return data;
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
      const w = Math.round(img.width * scale), h = Math.round(img.height * scale);
      const c = document.createElement('canvas'); c.width = w; c.height = h;
      c.getContext('2d').drawImage(img, 0, 0, w, h);
      resolve(c.toDataURL('image/jpeg', quality));
    };
    img.onerror = () => resolve(data);
    img.src = data;
  });
}

function ConditionPicker({ value, onChange, testid }) {
  return (
    <div className="flex gap-1" data-testid={testid}>
      {CONDITIONS.map((c) => (
        <button key={c} type="button" onClick={() => onChange(c)}
                className={`flex-1 py-1.5 text-xs rounded-md border ${value === c ? 'border-ink bg-ink text-white' : 'border-ink/15 text-ink hover:bg-page'}`}>
          {c}
        </button>
      ))}
    </div>
  );
}

function MinimalCard({ asset }) {
  const first = asset?.photo;
  return (
    <div className="max-w-md mx-auto" data-testid="asset-minimal-card">
      <Card className="shine-card p-6 rounded-xl text-center space-y-3">
        <div className="flex items-center justify-center">
          {first ? (
            <img src={first} alt="" className="w-32 h-32 rounded-lg object-cover border border-ink/10" />
          ) : (
            <div className="w-32 h-32 rounded-lg bg-page border border-ink/10 flex items-center justify-center text-ink-2">
              <Camera className="h-7 w-7" />
            </div>
          )}
        </div>
        <div>
          <div className="text-lg font-semibold text-ink">{asset.name}</div>
          <div className="text-xs text-ink-2 font-mono mt-0.5">{asset.asset_id}</div>
        </div>
        <div className="flex items-start gap-2 p-3 rounded-lg bg-blue-50 border border-blue-100 text-left">
          <Info className="h-4 w-4 text-blue-700 flex-shrink-0 mt-0.5" />
          <div className="text-xs text-blue-900">
            {asset.status_message || 'This item is assigned. Please return it to the office or contact your Head.'}
          </div>
        </div>
        <Button variant="outline" asChild size="sm" data-testid="minimal-go-my-assets">
          <Link to="/assets">Go to my assets</Link>
        </Button>
      </Card>
    </div>
  );
}

export default function AssetDetailPage() {
  const { id } = useParams();
  const { user, hasPerm } = useAuth();
  const navigate = useNavigate();
  const [asset, setAsset] = useState(null);
  const [loading, setLoading] = useState(true);
  const [photoIdx, setPhotoIdx] = useState(0);
  const [godowns, setGodowns] = useState([]);
  const [users, setUsers] = useState([]);
  const [cats, setCats] = useState([]);
  const [dialog, setDialog] = useState(null); // 'take' | 'return' | 'transfer' | 'reject' | 'edit' | null
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/assets/${id}`);
      setAsset(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load asset');
    } finally { setLoading(false); }
  }, [id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get('/assets/godowns').then((r) => setGodowns(r.data || []));
    api.get('/assets/users').then((r) => setUsers(r.data || []));
    api.get('/assets/categories').then((r) => setCats(r.data || []));
  }, []);

  if (loading) return <div className="max-w-[1000px] mx-auto px-4 py-10 text-sm text-ink-2">Loading…</div>;
  if (!asset)  return <div className="max-w-[1000px] mx-auto px-4 py-10 text-sm text-ink-2">Not found.</div>;
  if (asset.minimal) return <MinimalCard asset={asset} />;

  const photos = asset.photos || [];
  const first  = photos[photoIdx] || null;
  const isFixed = !!asset.is_fixed;
  const canSeeMoney = hasPerm('see_money');
  const canManage   = asset.can_manage;
  const canEdit     = asset.can_edit;
  const uid = user?.id;
  const isHolder = asset.holder_type === 'user' && asset.holder_id === uid;
  const isGodown = asset.holder_type === 'godown';
  const pt = asset.pending_transfer;
  const pr = asset.pending_return;

  const qrUrl = `${process.env.REACT_APP_BACKEND_URL}/api/assets/${asset.asset_id}/qr.png?t=${Date.now()}`;

  const openDialog = (type) => {
    setDialog(type);
    const defaultG = godowns.find((g) => g.is_default) || godowns[0];
    if (type === 'take') {
      setForm({ client_or_project: '', expected_return: '', condition: 'Good', note: '' });
    } else if (type === 'return') {
      setForm({ to_godown_id: defaultG?.id || '', condition: asset.condition || 'Good', accessories_checked: [], photo: null, note: '' });
    } else if (type === 'transfer') {
      setForm({ to_user_id: '', reason: 'Handover', condition: asset.condition || 'Good', accessories_going: [], photo: null, note: '' });
    } else if (type === 'reject') {
      setForm({ reason: '' });
    } else if (type === 'delete') {
      setForm({ reason: '' });
    } else if (type === 'edit') {
      setForm({
        name: asset.name || '', brand: asset.brand || '', model: asset.model || '',
        serial_no: asset.serial_no || '', condition: asset.condition || 'Good',
        accessories: asset.accessories || '', notes: asset.notes || '',
        category_id: asset.category_id || '',
        purchase_date: asset.purchase_date || '',
        purchase_cost: asset.purchase_cost ?? '',
        vendor: asset.vendor || '', warranty_end: asset.warranty_end || '',
        owning_entity: asset.owning_entity || '', is_fixed: !!asset.is_fixed,
        photos: [...(asset.photos || [])],
        invoice_file: asset.invoice_file || null,
        invoice_filename: asset.invoice_filename || null,
      });
    }
  };

  const callAction = async (path, body = null) => {
    setSaving(true);
    try {
      const { data } = body ? await api.post(path, body) : await api.post(path);
      toast.success('Done');
      setDialog(null);
      setAsset(data?.asset_id ? data : asset);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Action failed');
    } finally { setSaving(false); }
  };

  const confirmTransferAsReceiver = async () => callAction(`/assets/${asset.asset_id}/transfer/confirm`);
  const rejectTransfer           = async () => callAction(`/assets/${asset.asset_id}/transfer/reject`, { reason: form.reason });
  const cancelTransfer           = async () => callAction(`/assets/${asset.asset_id}/transfer/cancel`);
  const remindTransfer           = async () => callAction(`/assets/${asset.asset_id}/transfer/remind`);
  const confirmReturn            = async () => callAction(`/assets/${asset.asset_id}/return/confirm`);

  const submitDialog = async () => {
    if (dialog === 'take') {
      await callAction(`/assets/${asset.asset_id}/take`, form);
    } else if (dialog === 'return') {
      if (['Fair', 'Poor'].includes(form.condition) && !form.photo) return toast.error('Photo required for Fair/Poor');
      await callAction(`/assets/${asset.asset_id}/return`, form);
    } else if (dialog === 'transfer') {
      if (!form.to_user_id) return toast.error('Pick a receiver');
      await callAction(`/assets/${asset.asset_id}/transfer`, form);
    } else if (dialog === 'edit') {
      setSaving(true);
      try {
        const payload = { ...form };
        if (payload.purchase_cost === '' || payload.purchase_cost == null) delete payload.purchase_cost;
        else payload.purchase_cost = Number(payload.purchase_cost);
        await api.patch(`/assets/${asset.asset_id}`, payload);
        toast.success('Updated');
        setDialog(null);
        await load();
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'Save failed');
      } finally { setSaving(false); }
    } else if (dialog === 'delete') {
      if (!form.reason?.trim()) return toast.error('Reason is required');
      setSaving(true);
      try {
        const resp = await api.delete(`/assets/${asset.asset_id}`, { data: { reason: form.reason.trim() } });
        if (resp.data.warned_held) {
          toast.warning('Deleted — note that it was still held by a person; the holder was notified.');
        } else {
          toast.success('Asset deleted');
        }
        navigate('/assets');
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'Delete failed');
      } finally { setSaving(false); }
    }
  };

  const uploadPhoto = async (file, field) => {
    if (!file) return;
    const url = await fileToDataUrl(file);
    setForm((f) => ({ ...f, [field]: url }));
  };

  // Accessories: splice array of strings from asset.accessories (comma list)
  const accessoryOptions = (asset.accessories || '').split(/[,;]/).map((s) => s.trim()).filter(Boolean);

  return (
    <div className="max-w-[1100px] mx-auto px-4 sm:px-6 py-6 space-y-5" data-testid="asset-detail-page">
      <Link to="/assets" className="text-xs text-ink-2 inline-flex items-center gap-1 hover:text-ink">
        <ChevronLeft className="h-3 w-3" /> All assets
      </Link>

      {/* Pending-transfer banner */}
      {pt && (
        <Card className="shine-card p-4 rounded-xl bg-orange-50 border border-orange-200 flex items-start gap-3" data-testid="pending-transfer-banner">
          <ArrowRightLeft className="h-5 w-5 text-orange-700 flex-shrink-0 mt-0.5" />
          <div className="flex-1 text-sm">
            <div className="font-semibold text-orange-900">
              Transfer pending to {pt.to_user_name} · sent by {pt.requested_by_name}
            </div>
            <div className="text-xs text-orange-800 mt-0.5">
              Reason: {pt.reason} · Requested {fmtDateTime(pt.requested_at)}
            </div>
          </div>
          {pt.to_user_id === uid ? (
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => openDialog('reject')} data-testid="transfer-reject-open">Reject</Button>
              <Button size="sm" onClick={confirmTransferAsReceiver} disabled={saving} data-testid="transfer-confirm-receipt">
                <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Confirm receipt
              </Button>
            </div>
          ) : (
            <div className="flex gap-2">
              {canManage && <Button variant="outline" size="sm" onClick={remindTransfer} data-testid="transfer-remind">Remind</Button>}
              {(pt.requested_by === uid || canManage) &&
                <Button variant="outline" size="sm" onClick={cancelTransfer} data-testid="transfer-cancel">Cancel</Button>}
            </div>
          )}
        </Card>
      )}

      {/* Pending-return banner */}
      {pr && (
        <Card className="shine-card p-4 rounded-xl bg-amber-50 border border-amber-200 flex items-start gap-3" data-testid="pending-return-banner">
          <Clock className="h-5 w-5 text-amber-700 flex-shrink-0 mt-0.5" />
          <div className="flex-1 text-sm">
            <div className="font-semibold text-amber-900">Return pending — {pr.to_godown_name}</div>
            <div className="text-xs text-amber-800 mt-0.5">
              {pr.returning_user_name} is returning this — condition {pr.condition} · {fmtDateTime(pr.requested_at)}.
              {asset.can_confirm_return ? ' You can confirm this return.' : ' The godown keeper (or Admin) must confirm.'}
            </div>
          </div>
          {asset.can_confirm_return && (
            <Button size="sm" onClick={confirmReturn} disabled={saving} data-testid="return-confirm">
              <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Confirm return
            </Button>
          )}
        </Card>
      )}

      {/* Header */}
      <div className="flex flex-col lg:flex-row gap-5">
        <div className="lg:w-80 flex-shrink-0">
          <div className="aspect-square rounded-xl bg-page border border-ink/10 overflow-hidden">
            {first ? (
              <img src={first} alt="" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-ink-2"><Camera className="h-10 w-10" /></div>
            )}
          </div>
          {photos.length > 1 && (
            <div className="flex gap-1 mt-2 justify-center">
              {photos.map((_, i) => (
                <button key={i} onClick={() => setPhotoIdx(i)}
                        className={`h-2 w-2 rounded-full ${i === photoIdx ? 'bg-ink' : 'bg-ink/20'}`} />
              ))}
            </div>
          )}
        </div>

        <div className="flex-1 space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <Badge className="bg-slate-100 text-slate-700 font-mono">{asset.asset_id}</Badge>
            <Badge className={STATUS_COLOR[asset.status] || 'bg-slate-100 text-slate-700'}>{asset.status}</Badge>
            {isFixed && <Badge className="bg-slate-100 text-slate-700">Fixed</Badge>}
          </div>
          <div>
            <h1 className="text-2xl font-semibold text-ink" data-testid="asset-name">{asset.name}</h1>
            <div className="text-xs text-ink-2 mt-0.5">{[asset.brand, asset.model].filter(Boolean).join(' · ') || '—'}</div>
          </div>

          <div className="flex items-center gap-2 p-3 rounded-lg bg-page border border-ink/10" data-testid="asset-holder">
            <Package className="h-4 w-4 text-ink-2" />
            <div className="flex-1 text-sm">
              <span className="text-ink-2">{isGodown ? 'Stored at' : 'With'}: </span>
              <span className="text-ink font-medium">{asset.holder_name || '—'}</span>
              {asset.holder_role && <span className="text-ink-2"> · {asset.holder_role}</span>}
              {asset.holder_location && <span className="text-ink-2"> · {asset.holder_location}</span>}
              {asset.expected_return && <span className="text-ink-2"> · expected back {asset.expected_return}</span>}
              {asset.taken_client && <span className="text-ink-2"> · for {asset.taken_client}</span>}
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => openAssetLabelPdf(asset.asset_id)} data-testid="print-label">
              <Printer className="h-3.5 w-3.5 mr-1" /> Print label
            </Button>
            {canEdit && (
              <Button variant="outline" size="sm" onClick={() => openDialog('edit')} data-testid="edit-asset">
                <Edit className="h-3.5 w-3.5 mr-1" /> Edit
              </Button>
            )}
            {!isFixed && isGodown && !pr && !pt && (
              <Button size="sm" onClick={() => openDialog('take')} data-testid="take-asset">
                <Package className="h-3.5 w-3.5 mr-1" /> Take
              </Button>
            )}
            {!isFixed && isHolder && !pr && !pt && (
              <Button size="sm" onClick={() => openDialog('return')} data-testid="return-asset">
                <Package className="h-3.5 w-3.5 mr-1" /> Return
              </Button>
            )}
            {!isFixed && (isHolder || canManage) && !pr && !pt && (
              <Button variant="outline" size="sm" onClick={() => openDialog('transfer')} data-testid="transfer-asset">
                <ArrowRightLeft className="h-3.5 w-3.5 mr-1" /> Transfer
              </Button>
            )}
            {canEdit && (
              <Button variant="outline" size="sm"
                      onClick={() => navigate('/assets/new', { state: { duplicate: asset } })}
                      data-testid="duplicate-asset">
                <Copy className="h-3.5 w-3.5 mr-1" /> Duplicate
              </Button>
            )}
            {user?.role === 'Owner' && !asset.deleted && (
              <Button variant="outline" size="sm" className="border-rose-300 text-rose-700 hover:bg-rose-50"
                      onClick={() => openDialog('delete')} data-testid="delete-asset">
                <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete
              </Button>
            )}
          </div>
        </div>

        <div className="lg:w-56 flex-shrink-0">
          <Card className="shine-card p-3 rounded-xl text-center" data-testid="qr-panel">
            <div className="flex items-center justify-center gap-1 text-[11px] text-ink-2 mb-2">
              <QrCode className="h-3.5 w-3.5" /> Scan to open
            </div>
            {asset.qr_url ? (
              <>
                <img src={qrUrl} alt="QR" className="w-full h-auto bg-white rounded border border-ink/10 p-1" />
                <div className="text-[10px] text-ink-2 mt-2 break-all" data-testid="qr-url-text">{asset.qr_url}</div>
              </>
            ) : (
              <div className="p-3 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-lg" data-testid="qr-unconfigured">
                QR cannot be generated — ask an Admin to set the <b>Public base URL</b> in Assets → Setup.
              </div>
            )}
          </Card>
        </div>
      </div>

      {/* Details */}
      <Card className="shine-card p-5 rounded-xl" data-testid="asset-details">
        <div className="text-sm font-semibold mb-3">Details</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
          <Field k="Serial number" v={asset.serial_no} mono />
          <Field k="Condition"     v={asset.condition} />
          <Field k="Purchase date" v={asset.purchase_date} />
          <Field k="Warranty end"  v={asset.warranty_end} />
          <Field k="Owning company" v={asset.owning_entity} />
          <Field k="Accessories"   v={asset.accessories} />
          {canSeeMoney && (
            <>
              <Field k="Vendor" v={asset.vendor} />
              <Field k="Purchase cost" v={asset.purchase_cost == null ? '—' : `₹ ${Number(asset.purchase_cost).toLocaleString('en-IN')}`} tag="money" />
              {asset.invoice_file && (<Field k="Vendor invoice" v={<a className="text-blue-700 hover:underline" href={asset.invoice_file} download={asset.invoice_filename || 'invoice'}>{asset.invoice_filename || 'invoice'}</a>} />)}
            </>
          )}
          <Field k="Listed by" v={asset.listed_by_name || '—'} />
          <Field k="Notes" v={asset.notes} full />
        </div>
      </Card>

      {/* History */}
      <Card className="shine-card p-5 rounded-xl" data-testid="asset-history">
        <div className="flex items-center justify-between mb-3">
          <div className="text-sm font-semibold">History</div>
          <div className="text-[11px] text-ink-2">Newest first</div>
        </div>
        <ul className="space-y-2">
          {(asset.history || []).map((e) => {
            const meta = EVENT_META[e.type] || { icon: Info, label: e.type };
            const Icon = meta.icon;
            return (
              <li key={e.id} className="flex items-start gap-3 p-2 rounded-lg hover:bg-page">
                <div className="mt-0.5 h-7 w-7 rounded-full bg-blue-50 text-blue-700 flex items-center justify-center flex-shrink-0">
                  <Icon className="h-3.5 w-3.5" />
                </div>
                <div className="flex-1 min-w-0 text-xs">
                  <div className="text-ink">
                    <span className="font-medium">{meta.label}</span>
                    {e.by_user_name && <span className="text-ink-2"> — by {e.by_user_name}</span>}
                    {e.reason && <span className="text-ink-2"> — “{e.reason}”</span>}
                    {e.client_or_project && <span className="text-ink-2"> — for {e.client_or_project}</span>}
                    {e.note && <span className="text-ink-2"> — {e.note}</span>}
                  </div>
                  <div className="text-ink-2 mt-0.5">
                    {fmtDateTime(e.at)}
                    {e.condition_at_event && <span> · condition {e.condition_at_event}</span>}
                    {e.status === 'waiting_keeper' && <span> · waiting for keeper</span>}
                  </div>
                </div>
              </li>
            );
          })}
          {(asset.history || []).length === 0 && <li className="text-xs text-ink-2 py-3">No events yet.</li>}
        </ul>
      </Card>

      {/* ─── Take dialog ─── */}
      <Dialog open={dialog === 'take'} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="sm:max-w-md" data-testid="take-dialog">
          <DialogHeader><DialogTitle>Take this asset</DialogTitle></DialogHeader>
          <div className="text-xs text-ink-2 mb-3">{asset.name} ({asset.asset_id}) from {asset.holder_name}</div>
          <div className="space-y-3 text-sm">
            <div>
              <Label className="text-xs">Client or project</Label>
              <Input value={form.client_or_project || ''}
                     onChange={(e) => setForm({ ...form, client_or_project: e.target.value })}
                     placeholder="Merge Test Co — Diwali shoot" data-testid="take-client" />
            </div>
            <div>
              <Label className="text-xs">Expected return date</Label>
              <Input type="date" value={form.expected_return || ''}
                     onChange={(e) => setForm({ ...form, expected_return: e.target.value })} data-testid="take-expected" />
            </div>
            <div>
              <Label className="text-xs">Condition now</Label>
              <ConditionPicker value={form.condition} onChange={(c) => setForm({ ...form, condition: c })} testid="take-condition" />
            </div>
            <div>
              <Label className="text-xs">Note</Label>
              <Textarea rows={2} value={form.note || ''} onChange={(e) => setForm({ ...form, note: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(null)}>Cancel</Button>
            <Button onClick={submitDialog} disabled={saving} data-testid="take-submit">Take it</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── Return dialog ─── */}
      <Dialog open={dialog === 'return'} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="sm:max-w-md" data-testid="return-dialog">
          <DialogHeader><DialogTitle>Return asset</DialogTitle></DialogHeader>
          <div className="text-xs text-ink-2 mb-3">{asset.name} ({asset.asset_id})</div>
          <div className="space-y-3 text-sm">
            <div>
              <Label className="text-xs">Return to</Label>
              <Select value={form.to_godown_id || ''} onValueChange={(v) => setForm({ ...form, to_godown_id: v })}>
                <SelectTrigger data-testid="return-godown"><SelectValue placeholder="Pick a godown" /></SelectTrigger>
                <SelectContent>{godowns.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Condition</Label>
              <ConditionPicker value={form.condition} onChange={(c) => setForm({ ...form, condition: c })} testid="return-condition" />
            </div>
            {accessoryOptions.length > 0 && (
              <div>
                <Label className="text-xs">Accessories returned</Label>
                <div className="space-y-1">
                  {accessoryOptions.map((a) => (
                    <label key={a} className="flex items-center gap-2 text-xs cursor-pointer">
                      <input type="checkbox" checked={(form.accessories_checked || []).includes(a)}
                             onChange={(e) => {
                               const curr = form.accessories_checked || [];
                               setForm({ ...form, accessories_checked: e.target.checked ? [...curr, a] : curr.filter((x) => x !== a) });
                             }} />
                      {a}
                    </label>
                  ))}
                </div>
              </div>
            )}
            {['Fair', 'Poor'].includes(form.condition) && (
              <div>
                <Label className="text-xs">Photo <span className="text-rose-600">(required for Fair/Poor)</span></Label>
                {form.photo ? (
                  <div className="relative inline-block">
                    <img src={form.photo} alt="" className="h-24 rounded border border-ink/10" />
                    <button type="button" onClick={() => setForm({ ...form, photo: null })} className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-0.5"><X className="h-3 w-3" /></button>
                  </div>
                ) : (
                  <label className="inline-flex items-center gap-2 text-xs cursor-pointer px-3 py-2 rounded-lg border border-dashed border-ink/20 hover:border-ink/40">
                    <Upload className="h-3.5 w-3.5" /> + Add photo
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadPhoto(e.target.files?.[0], 'photo')} />
                  </label>
                )}
              </div>
            )}
            <div>
              <Label className="text-xs">Note</Label>
              <Textarea rows={2} value={form.note || ''} onChange={(e) => setForm({ ...form, note: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(null)}>Cancel</Button>
            <Button onClick={submitDialog} disabled={saving} data-testid="return-submit">Return</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── Transfer dialog ─── */}
      <Dialog open={dialog === 'transfer'} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="sm:max-w-md" data-testid="transfer-dialog">
          <DialogHeader><DialogTitle>Transfer asset</DialogTitle></DialogHeader>
          <div className="text-xs text-ink-2 mb-3">{asset.name} ({asset.asset_id}) · with {asset.holder_name}</div>
          <div className="space-y-3 text-sm">
            <div>
              <Label className="text-xs">Give to</Label>
              <Select value={form.to_user_id || ''} onValueChange={(v) => setForm({ ...form, to_user_id: v })}>
                <SelectTrigger data-testid="transfer-receiver"><SelectValue placeholder="Pick a person" /></SelectTrigger>
                <SelectContent>{users.filter((u) => u.id !== uid).map((u) => <SelectItem key={u.id} value={u.id}>{u.name} · {u.role}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Reason</Label>
              <div className="flex flex-wrap gap-1">
                {TRANSFER_REASONS.map((r) => (
                  <button key={r} type="button" onClick={() => setForm({ ...form, reason: r })}
                          className={`px-2.5 py-1 text-xs rounded-full border ${form.reason === r ? 'border-ink bg-ink text-white' : 'border-ink/15 text-ink hover:bg-page'}`}
                          data-testid={`transfer-reason-${r.toLowerCase().replace(/[^a-z]/g, '-')}`}>{r}</button>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-xs">Condition</Label>
              <ConditionPicker value={form.condition} onChange={(c) => setForm({ ...form, condition: c })} testid="transfer-condition" />
            </div>
            {accessoryOptions.length > 0 && (
              <div>
                <Label className="text-xs">Accessories going with it</Label>
                <div className="space-y-1">
                  {accessoryOptions.map((a) => (
                    <label key={a} className="flex items-center gap-2 text-xs cursor-pointer">
                      <input type="checkbox" checked={(form.accessories_going || []).includes(a)}
                             onChange={(e) => {
                               const curr = form.accessories_going || [];
                               setForm({ ...form, accessories_going: e.target.checked ? [...curr, a] : curr.filter((x) => x !== a) });
                             }} />
                      {a}
                    </label>
                  ))}
                </div>
              </div>
            )}
            {['Fair', 'Poor'].includes(form.condition) && (
              <div>
                <Label className="text-xs">Photo <span className="text-rose-600">(required for Fair/Poor)</span></Label>
                {form.photo ? (
                  <div className="relative inline-block">
                    <img src={form.photo} alt="" className="h-24 rounded border border-ink/10" />
                    <button type="button" onClick={() => setForm({ ...form, photo: null })} className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-0.5"><X className="h-3 w-3" /></button>
                  </div>
                ) : (
                  <label className="inline-flex items-center gap-2 text-xs cursor-pointer px-3 py-2 rounded-lg border border-dashed border-ink/20 hover:border-ink/40">
                    <Upload className="h-3.5 w-3.5" /> + Add photo
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadPhoto(e.target.files?.[0], 'photo')} />
                  </label>
                )}
              </div>
            )}
            <p className="text-[11px] text-ink-2">
              The receiver gets a notification and must <b>Confirm receipt</b>. Until then, this asset stays with you.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(null)}>Cancel</Button>
            <Button onClick={submitDialog} disabled={saving} data-testid="transfer-submit">
              <Send className="h-3.5 w-3.5 mr-1" /> Send transfer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── Reject-transfer dialog ─── */}
      <Dialog open={dialog === 'reject'} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="sm:max-w-md" data-testid="reject-dialog">
          <DialogHeader><DialogTitle>Reject this transfer</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <Textarea rows={3} placeholder="Reason for rejection (optional)" value={form.reason || ''}
                      onChange={(e) => setForm({ ...form, reason: e.target.value })} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(null)}>Cancel</Button>
            <Button onClick={rejectTransfer} disabled={saving} data-testid="reject-submit">Reject</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── Edit dialog (lister within 24h / assets_manage) ─── */}
      <Dialog open={dialog === 'edit'} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto" data-testid="edit-dialog">
          <DialogHeader><DialogTitle>Edit asset</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <div><Label className="text-xs">Name</Label>
              <Input value={form.name || ''} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <div><Label className="text-xs">Category</Label>
                <select className="w-full h-10 rounded-md border border-ink/15 px-3 text-sm bg-white"
                        value={form.category_id || ''}
                        onChange={(e) => setForm({ ...form, category_id: e.target.value })}
                        data-testid="edit-category">
                  {cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-xs">Owning company</Label>
                <Input value={form.owning_entity || ''} onChange={(e) => setForm({ ...form, owning_entity: e.target.value })} />
              </div>
              <div><Label className="text-xs">Brand</Label>
                <Input value={form.brand || ''} onChange={(e) => setForm({ ...form, brand: e.target.value })} /></div>
              <div><Label className="text-xs">Model</Label>
                <Input value={form.model || ''} onChange={(e) => setForm({ ...form, model: e.target.value })} /></div>
              <div><Label className="text-xs">Serial number</Label>
                <Input value={form.serial_no || ''} onChange={(e) => setForm({ ...form, serial_no: e.target.value })} /></div>
              <div><Label className="text-xs">Condition</Label>
                <ConditionPicker value={form.condition} onChange={(c) => setForm({ ...form, condition: c })} /></div>
              <div>
                <Label className="text-xs">Purchase date</Label>
                <Input type="date" value={form.purchase_date || ''} onChange={(e) => setForm({ ...form, purchase_date: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">Warranty end</Label>
                <Input type="date" value={form.warranty_end || ''} onChange={(e) => setForm({ ...form, warranty_end: e.target.value })} />
              </div>
              {hasPerm('see_money') && (
                <>
                  <div>
                    <Label className="text-xs">Purchase cost</Label>
                    <Input type="number" min="0" step="1" value={form.purchase_cost ?? ''}
                           onChange={(e) => setForm({ ...form, purchase_cost: e.target.value })}
                           data-testid="edit-cost" />
                  </div>
                  <div>
                    <Label className="text-xs">Vendor</Label>
                    <Input value={form.vendor || ''} onChange={(e) => setForm({ ...form, vendor: e.target.value })} />
                  </div>
                </>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Label className="text-xs">Fixed item?</Label>
              <Switch checked={!!form.is_fixed} onCheckedChange={(v) => setForm({ ...form, is_fixed: !!v })} data-testid="edit-is-fixed" />
              <span className="text-xs text-ink-2">{form.is_fixed ? 'Yes — stays put' : 'No — can be taken out'}</span>
            </div>
            <div>
              <Label className="text-xs">Accessories</Label>
              <Input value={form.accessories || ''} onChange={(e) => setForm({ ...form, accessories: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs">Notes</Label>
              <Textarea rows={2} value={form.notes || ''} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs">Photos ({(form.photos || []).length}/4)</Label>
              <div className="grid grid-cols-4 gap-2">
                {(form.photos || []).map((p, i) => (
                  <div key={i} className="relative aspect-square rounded-lg overflow-hidden border border-ink/10">
                    <img src={p} alt="" className="w-full h-full object-cover" />
                    <button type="button"
                            onClick={() => setForm({ ...form, photos: form.photos.filter((_, j) => j !== i) })}
                            className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-0.5"
                            aria-label="Remove photo" data-testid={`edit-remove-photo-${i}`}>
                      <X className="h-3 w-3" />
                    </button>
                    <label className="absolute bottom-1 right-1 bg-black/60 text-white rounded-full p-0.5 cursor-pointer" title="Replace">
                      <Upload className="h-3 w-3" />
                      <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
                        const f = e.target.files?.[0]; if (!f) return;
                        const url = await fileToDataUrl(f);
                        const next = [...form.photos]; next[i] = url;
                        setForm({ ...form, photos: next });
                      }} />
                    </label>
                  </div>
                ))}
                {(form.photos || []).length < 4 && (
                  <label className="aspect-square rounded-lg border border-dashed border-ink/20 flex items-center justify-center text-ink-2 hover:text-ink hover:border-ink/40 cursor-pointer" data-testid="edit-add-photo">
                    <Camera className="h-5 w-5" />
                    <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
                      const f = e.target.files?.[0]; if (!f) return;
                      const url = await fileToDataUrl(f);
                      setForm({ ...form, photos: [...(form.photos || []), url] });
                    }} />
                  </label>
                )}
              </div>
            </div>
            {hasPerm('see_money') && (
              <div>
                <Label className="text-xs">Invoice file</Label>
                {form.invoice_file ? (
                  <div className="flex items-center gap-2 text-xs p-2 rounded-lg bg-page border border-ink/10">
                    <span className="flex-1 truncate">{form.invoice_filename || 'Attached'}</span>
                    <label className="cursor-pointer text-blue-700 hover:underline">Replace
                      <input type="file" accept=".pdf,image/*" className="hidden" onChange={async (e) => {
                        const f = e.target.files?.[0]; if (!f) return;
                        const url = await fileToDataUrl(f, 2400, 0.85);
                        setForm({ ...form, invoice_file: url, invoice_filename: f.name });
                      }} />
                    </label>
                    <button type="button" className="text-rose-700 hover:underline"
                            onClick={() => setForm({ ...form, invoice_file: null, invoice_filename: null })}>Remove</button>
                  </div>
                ) : (
                  <label className="inline-flex items-center gap-2 text-xs px-3 py-2 rounded-lg border border-dashed border-ink/20 cursor-pointer hover:border-ink/40">
                    <Upload className="h-3.5 w-3.5" /> Attach PDF or image
                    <input type="file" accept=".pdf,image/*" className="hidden" onChange={async (e) => {
                      const f = e.target.files?.[0]; if (!f) return;
                      const url = await fileToDataUrl(f, 2400, 0.85);
                      setForm({ ...form, invoice_file: url, invoice_filename: f.name });
                    }} />
                  </label>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(null)}>Cancel</Button>
            <Button onClick={submitDialog} disabled={saving} data-testid="edit-submit">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── Delete dialog (Owner only, soft delete) ─── */}
      <Dialog open={dialog === 'delete'} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="sm:max-w-md" data-testid="delete-dialog">
          <DialogHeader><DialogTitle className="text-rose-700">Delete this asset?</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            {(asset.holder_type === 'user' || pt || pr) && (
              <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-900">
                <AlertCircle className="h-3.5 w-3.5 inline mr-1" />
                <b>Heads up:</b> this asset is {pt ? 'in a pending transfer' : pr ? 'in a pending return' : `currently held by ${asset.holder_name}`}.
                Deleting it will skip these flows. The current holder (if any) will be notified.
              </div>
            )}
            <div>
              <Label className="text-xs">Reason (required)</Label>
              <Textarea rows={3} placeholder="Why are you deleting it? (sold, lost, scrapped, data error…)"
                        value={form.reason || ''}
                        onChange={(e) => setForm({ ...form, reason: e.target.value })}
                        data-testid="delete-reason" />
            </div>
            <p className="text-[11px] text-ink-2">
              This is a <b>soft delete</b>. The asset will disappear from every list but remain in your <b>Deleted assets</b> view with a Restore button and a final "Delete permanently" option. The ID is never reused.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(null)}>Cancel</Button>
            <Button className="bg-rose-600 hover:bg-rose-700 text-white"
                    onClick={submitDialog} disabled={saving || !form.reason?.trim()}
                    data-testid="delete-confirm">Delete asset</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ k, v, mono, tag, full }) {
  if (v == null || v === '') v = '—';
  return (
    <div className={full ? 'sm:col-span-2' : ''}>
      <div className="text-[11px] text-ink-2">{k}{tag && <span className="ml-1 text-[10px] text-amber-700">· {tag}</span>}</div>
      <div className={`text-sm text-ink ${mono ? 'font-mono' : ''}`}>{v}</div>
    </div>
  );
}
